import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  collectionGroup,
  getDoc,
  doc as firestoreDoc,
  orderBy,
} from "firebase/firestore";
import {
  getDatabase,
  ref,
  onValue,
  DatabaseReference,
} from "firebase/database";
import { Ambulance } from "../types/ambulance";

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyB8iwNdOkhSGphpenrNs2QZzkQ4Ra0I558",
  authDomain: "ladderemsrn.firebaseapp.com",
  databaseURL: "https://ladderemsrn-default-rtdb.firebaseio.com",
  projectId: "ladderemsrn",
  storageBucket: "ladderemsrn.appspot.com",
  messagingSenderId: "723883265845",
  appId: "1:723883265845:web:716064da339b18bbd6867c",
  measurementId: "G-2CFH80WDVF",
  // apiKey: "AIzaSyDEYcdHgZwlpq3QxIRqtT8G3NmodCSwhiM",
  // authDomain: "ladderdashboard.firebaseapp.com",
  // databaseURL: "https://ladderdashboard-default-rtdb.firebaseio.com",
  // projectId: "ladderdashboard",
  // storageBucket: "ladderdashboard.appspot.com",
  // messagingSenderId: "397799914957",
  // appId: "1:397799914957:web:092e7ee3e4585d39e2d884",
  // measurementId: "G-KFEEX7PJ3H",
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const rtdb = getDatabase(app);

// Hospital constants
export const HUNTINGTON_HOSPITAL_ID = "huntington";
export const HUNTINGTON_HOSPITAL_POSITION = {
  lat: 40.868092, // Approximate coordinates for Huntington Hospital, NY
  lng: -73.423981,
};

// Cache for unit locations and ETAs to prevent repeated calls
const locationCache = new Map<string, { lat: number; lng: number } | null>();
const etaCache = new Map<string, { eta: number | null; timestamp: number }>();
const ETA_CACHE_EXPIRY = 60000; // 60 seconds in milliseconds

// Track active location listeners
const activeLocationListeners = new Map<string, DatabaseReference>();

// Cache for geocoded addresses
const geocodeCache = new Map<string, { lat: number; lng: number } | null>();
const GEOCODE_CACHE_EXPIRY = 3600000; // 1 hour in milliseconds

// Get ETA using OpenStreetMap Routing Machine (OSRM) API
const getOSRMEta = async (
  unitId: string,
  startLat: number,
  startLng: number
): Promise<number | null> => {
  // Check cache first
  const cachedData = etaCache.get(unitId);
  const now = Date.now();

  if (cachedData && now - cachedData.timestamp < ETA_CACHE_EXPIRY) {
    return cachedData.eta;
  }

  try {
    const endLat = HUNTINGTON_HOSPITAL_POSITION.lat;
    const endLng = HUNTINGTON_HOSPITAL_POSITION.lng;

    // Use OSRM public API to get driving route duration
    const url = `https://router.project-osrm.org/route/v1/driving/${startLng},${startLat};${endLng},${endLat}?overview=false`;

    // Create an AbortController to enable a timeout
    const controller = new AbortController();
    const signal = controller.signal;

    // Set a timeout of 5 seconds
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 5000);

    try {
      const response = await fetch(url, { signal });
      clearTimeout(timeoutId);

      const data = await response.json();

      let etaValue = null;
      if (data.routes && data.routes.length > 0) {
        // Duration is in seconds
        etaValue = Math.round(data.routes[0].duration);
      }

      // Update cache
      etaCache.set(unitId, { eta: etaValue, timestamp: now });
      return etaValue;
    } catch (fetchError: any) {
      clearTimeout(timeoutId);

      if (fetchError.name === "AbortError") {
        console.log(`ETA fetch aborted for ${unitId} due to timeout`);
      } else {
        console.error("Error fetching ETA from OSRM:", fetchError);
      }

      // Still cache the failed attempt to prevent repeated failures
      etaCache.set(unitId, { eta: null, timestamp: now });
      return null;
    }
  } catch (error) {
    console.error("Error in getOSRMEta:", error);
    // Still cache the failed attempt to prevent repeated failures
    etaCache.set(unitId, { eta: null, timestamp: now });
    return null;
  }
};

// Get coordinates from address using Nominatim (OpenStreetMap's geocoding service)
const getCoordinatesFromAddress = async (
  address: string,
  town: string
): Promise<{ lat: number; lng: number } | null> => {
  // Normalize and create a cache key from the address + town
  const cacheKey = `${address} ${town}`.trim().toLowerCase();

  // Check cache first
  const cachedCoordinates = geocodeCache.get(cacheKey);
  if (cachedCoordinates) {
    return cachedCoordinates;
  }

  try {
    // Format the query string for Nominatim
    const query = encodeURIComponent(`${address}, ${town}, NY, USA`);

    // Call Nominatim API (respecting usage policy)
    const url = `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`;

    const controller = new AbortController();
    const signal = controller.signal;

    // Set a timeout of 5 seconds
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 5000);

    const response = await fetch(url, {
      signal,
      headers: {
        "User-Agent": "HuntingtonHospitalAmbulanceDashboard/1.0",
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Geocoding API returned ${response.status}: ${response.statusText}`
      );
    }

    const data = await response.json();

    if (data && data.length > 0) {
      const coordinates = {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
      };

      // Cache the result
      geocodeCache.set(cacheKey, coordinates);

      return coordinates;
    } else {
      console.log(
        `No geocoding results found for address: ${address}, ${town}`
      );
      // Cache the negative result too to avoid repeated failures
      geocodeCache.set(cacheKey, null);
      return null;
    }
  } catch (error) {
    console.error(`Error geocoding address "${address}, ${town}":`, error);
    // Don't cache errors as they might be temporary
    return null;
  }
};

// Store for ambulance data
const ambulanceStore = new Map<string, Ambulance>();

// Callbacks for location updates
const locationUpdateCallbacks: ((
  unitId: string,
  location: { lat: number; lng: number } | null
) => void)[] = [];

// Get location data for a specific unit from Realtime Database
// Now returns initial location and sets up a continuous listener
const getUnitLocation = (
  unitId: string,
  agencyName: string = "Chestertown",
  alarmData: any,
  onUpdate?: (location: { lat: number; lng: number } | null) => void,
  checkOnly: boolean = false
): Promise<{ lat: number; lng: number } | null> => {
  console.log(`[RTDB] Looking up location for unit ${unitId} from agency "${agencyName}"`);

  // Create a cache key
  const cacheKey = `${agencyName}:${unitId}`;
  
  // If we're just checking, return the cached value if available
  if (checkOnly) {
    const cachedLocation = locationCache.get(cacheKey);
    return Promise.resolve(cachedLocation || null);
  }

  return new Promise((resolve, reject) => {
    // Set a timeout to ensure this promise doesn't hang indefinitely
    const timeoutId = setTimeout(() => {
      resolve(null); // Resolve with null instead of hanging
    }, 5000);

    try {
      unitId = unitId.replaceAll("-", "_");
      // Create the location reference
      const locationRef = ref(rtdb, `UnitLocation/${agencyName}/${unitId}`);
      console.log(`[RTDB] Accessing path: UnitLocation/${agencyName}/${unitId}`);

      // Set up a real-time listener for this unit's location
      const callback = (snapshot: any) => {
        clearTimeout(timeoutId); // Clear the timeout since we got a response

        const data = snapshot.val();
        let location: any = null;

        if (data && data.latitude && data.longitude) {
          location = {
            lat: data.latitude,
            lng: data.longitude,
          };
          console.log(`[RTDB] Received location data for ${agencyName}/${unitId}:`, location);
        } else {
          console.log(`[RTDB] No location data found for ${agencyName}/${unitId}`);
        }

        // Cache the result
        locationCache.set(cacheKey, location);

        // Call the update callback if provided
        if (onUpdate) {
          onUpdate(location);
        }

        // Call any registered global callbacks
        locationUpdateCallbacks.forEach((cb) => cb(unitId, location));

        // Only resolve the promise on the first call
        if (activeLocationListeners.has(cacheKey)) {
          resolve(location);
        }
      };

      // Register this as an active listener
      activeLocationListeners.set(cacheKey, locationRef);

      // Set up the listener - NOT using onlyOnce to get continuous updates
      onValue(locationRef, callback, (error) => {
        clearTimeout(timeoutId);
        console.error(`Error fetching location for ${unitId}:`, error);
        resolve(null); // Resolve with null on error
      });
    } catch (error) {
      clearTimeout(timeoutId);
      console.error(`Exception in getUnitLocation for ${unitId}:`, error);
      resolve(null); // Resolve with null on error
    }
  });
};

// Function to register a callback for location updates
export const onLocationUpdate = (
  callback: (
    unitId: string,
    location: { lat: number; lng: number } | null
  ) => void
) => {
  locationUpdateCallbacks.push(callback);
  return () => {
    const index = locationUpdateCallbacks.indexOf(callback);
    if (index > -1) {
      locationUpdateCallbacks.splice(index, 1);
    }
  };
};

// Keep track of processed document IDs to prevent duplication
const processedDocIds = new Set<string>();

// Also track unit IDs we've seen to ensure we don't process duplicates
const processedUnitIds = new Set<string>();

// Function to get ambulances en route to the specified hospital
export const getAmbulancesEnRoute = (
  hospitalId: string,
  callback: (ambulances: Ambulance[]) => void
) => {
  // Use collectionGroup to query all PSAPTIMES collections regardless of their path
  const psapTimesRef = collectionGroup(db, "PSAPTIMES");

  // Include both "Transporting" and "At Transport Destination" status
  // Query to get ambulances en route to or arrived at the specified hospital
  const q = query(
    psapTimesRef,
    // Replace the exact match with a more flexible approach
    // that checks for any destination containing Huntington Hospital
    where("UnitDestination", "in", [
      "@HUNT : 270 PARK AV HUNTIN",
      "270 PARK AV HUNTIN: @HUNTINGTON HOSPITAL: @HUNTINGTON HOSPITAL: @HUNT",
      "270 PARK AV HUNTIN: @HUNTINGTON HOSPITAL: @HUNTINGTON HOSPITAL",
      "@HUNTINGTON HOSPITAL",
      "@HUNTINGTON HOSPITAL: @HUNT"
    ]),
    where("UnitStatus", "in", ["Transporting", "At Transport Destination"]),
    orderBy("TimeStamp", "desc") // Include both statuses
  );

  // Time to keep arrived ambulances in the list (5 minutes)
  const ARRIVAL_DISPLAY_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Set up a handler for location updates
  const handleLocationUpdate = (
    unitId: string,
    location: { lat: number; lng: number } | null
  ) => {
    // If we have this ambulance in our store, update its location
    if (ambulanceStore.has(unitId)) {
      const ambulance = ambulanceStore.get(unitId)!;

      // Update location details
      const updatedAmbulance = {
        ...ambulance,
        lat: location?.lat || 0,
        lng: location?.lng || 0,
        hasLocation: !!location,
        // Important: Preserve the isNew and arrived state during location updates
        isNew: ambulance.isNew,
        arrived: ambulance.arrived,
        arrivalTime: ambulance.arrivalTime,
      };

      // If location exists, update ETA (only for ambulances that haven't arrived yet)
      if (location && !ambulance.arrived) {
        // Update ETA asynchronously
        getOSRMEta(unitId, location.lat, location.lng).then((eta) => {
          const ambulanceWithEta = {
            ...updatedAmbulance,
            eta: eta,
          };

          // Update the store
          ambulanceStore.set(unitId, ambulanceWithEta);

          // Get all ambulances and make sure they're properly sorted
          const allAmbulances = Array.from(ambulanceStore.values());

          // Sort ambulances:
          // 1. Non-arrived ambulances first (new ones at top, then by ETA)
          // 2. Arrived ambulances at the bottom
          allAmbulances.sort((a, b) => {
            // Arrived ambulances go to the bottom
            if (a.arrived && !b.arrived) return 1;
            if (!a.arrived && b.arrived) return -1;

            // If both have the same arrival status, sort normally
            if (!a.arrived && !b.arrived) {
              // New ambulances come first
              if (a.isNew && !b.isNew) return -1;
              if (!a.isNew && b.isNew) return 1;

              // Then sort by ETA if available
              if (a.eta && b.eta) return a.eta - b.eta;
              if (a.eta && !b.eta) return -1;
              if (!a.eta && b.eta) return 1;
            } else if (a.arrived && b.arrived) {
              // Both arrived, sort by arrival time (most recent first)
              return (
                (b.arrivalTime?.getTime() || 0) -
                (a.arrivalTime?.getTime() || 0)
              );
            }

            return 0;
          });

          // Clean up expired arrived ambulances
          const now = new Date();
          const filteredAmbulances = allAmbulances.filter((amb) => {
            if (amb.arrived && amb.arrivalTime) {
              const elapsed = now.getTime() - amb.arrivalTime.getTime();
              return elapsed < ARRIVAL_DISPLAY_DURATION;
            }
            return true;
          });

          // Trigger a callback with all current ambulances
          callback(filteredAmbulances);
        });
      } else {
        // Just update the ambulance without ETA
        ambulanceStore.set(unitId, updatedAmbulance);

        // Get all ambulances and make sure they're properly sorted
        const allAmbulances = Array.from(ambulanceStore.values());

        // Sort ambulances:
        // 1. Non-arrived ambulances first (new ones at top, then by ETA)
        // 2. Arrived ambulances at the bottom
        allAmbulances.sort((a, b) => {
          // Arrived ambulances go to the bottom
          if (a.arrived && !b.arrived) return 1;
          if (!a.arrived && b.arrived) return -1;

          // If both have the same arrival status, sort normally
          if (!a.arrived && !b.arrived) {
            // New ambulances come first
            if (a.isNew && !b.isNew) return -1;
            if (!a.isNew && b.isNew) return 1;

            // Then sort by ETA if available
            if (a.eta && b.eta) return a.eta - b.eta;
            if (a.eta && !b.eta) return -1;
            if (!a.eta && b.eta) return 1;
          } else if (a.arrived && b.arrived) {
            // Both arrived, sort by arrival time (most recent first)
            return (
              (b.arrivalTime?.getTime() || 0) - (a.arrivalTime?.getTime() || 0)
            );
          }

          return 0;
        });

        // Clean up expired arrived ambulances
        const now = new Date();
        const filteredAmbulances = allAmbulances.filter((amb) => {
          if (amb.arrived && amb.arrivalTime) {
            const elapsed = now.getTime() - amb.arrivalTime.getTime();
            return elapsed < ARRIVAL_DISPLAY_DURATION;
          }
          return true;
        });

        // Trigger a callback with all current ambulances
        callback(filteredAmbulances);
      }
    }
  };

  // Register for location updates
  const unsubscribeFromLocationUpdates = onLocationUpdate(handleLocationUpdate);

  // Set up real-time listener for ambulance status
  const unsubscribeFromFirestore = onSnapshot(q, async (snapshot) => {
    // Debug: Log all the different statuses in the snapshot
    const statuses = new Set<string>();
    snapshot.docs.forEach((doc) => {
      const status = doc.data().UnitStatus;
      statuses.add(status);
    });

    // Get unique unit IDs from the snapshot
    const uniqueUnitIds = new Set<string>();
    snapshot.docs.forEach((doc) => {
      const unitId = doc.data().UnitId || "Unknown";
      uniqueUnitIds.add(unitId);
    });

    // Get current unit IDs from the latest snapshot
    const currentUnitIds = new Set<string>(Array.from(uniqueUnitIds));

    // Remove ambulances that are no longer in the results
    Array.from(ambulanceStore.keys()).forEach((unitId) => {
      if (!currentUnitIds.has(unitId)) {
        ambulanceStore.delete(unitId);
      }
    });

    // Get the most recent document for each unit ID
    const unitDocsMap = new Map<string, any>();
    snapshot.docs.forEach((doc) => {
      const unitId = doc.data().UnitId || "Unknown";
      const existingDoc = unitDocsMap.get(unitId);

      // If we don't have a document for this unit yet, or this document is newer
      if (
        !existingDoc ||
        (doc.data().Timestamp &&
          existingDoc.data().Timestamp &&
          doc.data().Timestamp.toDate() > existingDoc.data().Timestamp.toDate())
      ) {
        unitDocsMap.set(unitId, doc);
      }
    });

    // Process each unique unit ID with its most recent document
    const ambulancesPromises = Array.from(unitDocsMap.entries()).map(
      async ([unitId, doc]) => {
        try {
          // Track this unit as processed
          processedUnitIds.add(unitId);

          const data = doc.data();
          const path = doc.ref.path;
          const pathParts = path.split("/");
          const agencyName =
            pathParts.length > 0
              ? pathParts[0].replaceAll("Alarms", "").trim()
              : "Chestertown";

          console.log(`[AgencyName] Extracted from path: "${agencyName}" (path: ${path})`);

          const alarmDocId = pathParts[1];
          const alarmDoc = await getDoc(
            firestoreDoc(db, agencyName + " Alarms", alarmDocId)
          );
          const alarmData = alarmDoc.data();

          if (
            alarmData?.AlarmStatus === "false" ||
            alarmData?.AlarmStatus === false
          ) {
            return;
          }

          // Fetch location from Realtime Database
          let location = null;
          let eta = null;
          let isGeocodedLocation = false; // Flag to track if we used geocoding

          try {
            console.log(`[AgencyName] Fetching location for unit ${unitId} from agency "${agencyName}"`);
            // Get unit location and set up continuous updates
            location = await getUnitLocation(unitId, agencyName, alarmData);

            // If no location from RTDB, try using the address from alarmData
            if (!location && alarmData && alarmData.Address && alarmData.Town) {
              // Get coordinates from address using Nominatim
              location = await getCoordinatesFromAddress(
                alarmData.Address,
                alarmData.Town
              );

              if (location) {
                isGeocodedLocation = true;
              }
            }

            if (location) {
              // Get ETA from OpenStreetMap routing service
              eta = await getOSRMEta(unitId, location.lat, location.lng);
            }
          } catch (error) {
            console.error(`Error fetching location for unit ${unitId}:`, error);
          }

          // Check if we already have this ambulance and preserve its isNew status
          const existingAmbulance = ambulanceStore.get(unitId);
          const isNew = existingAmbulance ? existingAmbulance.isNew : false;

          // Check if the ambulance has arrived at the hospital
          const isArrived = data.UnitStatus === "At Transport Destination";

          // Make sure we have a proper Date object for the timestamp
          const timestamp = data.TimeStamp?.toDate
            ? data.TimeStamp.toDate()
            : data.TimeStamp instanceof Date
            ? data.TimeStamp
            : new Date();

          // Preserve existing arrival time or use the document timestamp for newly arrived ambulances
          let arrivalTime = existingAmbulance?.arrivalTime;

          // For regular arrived ambulances, set the arrival time to the document timestamp
          if (isArrived && (!existingAmbulance || !existingAmbulance.arrived)) {
            arrivalTime = timestamp;
          }

          // For geocoded ambulances, check if the ETA has passed and mark as possibly arrived
          let isPossiblyArrived = false;
          if (isGeocodedLocation && eta !== null && !isArrived) {
            // If this is a geocoded location and not already marked as arrived
            const now = new Date();
            const etaDate = new Date(timestamp.getTime() + eta * 1000);

            // Check if the ETA has passed
            if (now > etaDate) {
              // The ambulance has possibly arrived based on its ETA
              isPossiblyArrived = true;

              // If we don't have an arrival time yet, set it now
              if (!arrivalTime) {
                arrivalTime = etaDate;
              }
            } else {
              // Reset the possibly arrived state if the ambulance was previously marked
              isPossiblyArrived = false;
              // Preserve the previous state if it exists
              if (existingAmbulance?.possiblyArrived) {
                isPossiblyArrived = existingAmbulance.possiblyArrived;
                arrivalTime = existingAmbulance.arrivalTime;
              }
            }
          } else if (existingAmbulance?.possiblyArrived) {
            // Preserve the possibly arrived state if it was already set
            isPossiblyArrived = existingAmbulance.possiblyArrived;
            arrivalTime = existingAmbulance.arrivalTime || arrivalTime;
          }
          console.log(alarmData);
          const ambulance = {
            id: doc.id,
            unitId: unitId,
            lat: location?.lat || 0,
            lng: location?.lng || 0,
            hasLocation: !!location,
            locationSource: location
              ? isGeocodedLocation
                ? ("geocoded" as const)
                : ("rtdb" as const)
              : null,
            alarmId: alarmData?.RunNumber || "",
            agencyName: agencyName,
            Agency: alarmData?.Agency || "Unknown",
            callType: alarmData?.CallType || "Unknown",
            status: data.UnitStatus || "Unknown",
            eta: isArrived || isPossiblyArrived ? 0 : eta, // Set ETA to 0 if arrived or possibly arrived
            destinationHospital: data.UnitDestination || "",
            timestamp: timestamp,
            isNew: isNew, // Preserve isNew status
            arrived: isArrived, // Set arrived status
            possiblyArrived: isPossiblyArrived, // Set possibly arrived status
            arrivalTime: arrivalTime, // Set arrival time if available
            address: isGeocodedLocation
              ? `${alarmData?.Address}, ${alarmData?.Town}`
              : undefined,
          };

          // Store the ambulance in our map for quick updates
          ambulanceStore.set(unitId, ambulance);

          return ambulance;
        } catch (error) {
          console.error(`Error processing ambulance ${unitId}:`, error);
          // Return a basic ambulance object to prevent Promise.all from failing
          return {
            id: doc.id,
            unitId: unitId,
            lat: 0,
            lng: 0,
            hasLocation: false,
            status: "Error",
            eta: null,
            destinationHospital: "",
            timestamp: new Date(),
            isNew: false,
          };
        }
      }
    );

    // Process only if we have unique units to process
    if (ambulancesPromises.length > 0) {
      try {
        const ambulances = await Promise.all(ambulancesPromises);

        // Filter out undefined values
        const filteredAmbulances = ambulances.filter(
          (a) => a !== undefined
        ) as Ambulance[];

        // Clean up expired arrived ambulances (those that arrived more than 5 minutes ago)
        const now = new Date();

        const finalAmbulances = filteredAmbulances.filter((amb) => {
          // Handle both actually arrived and possibly arrived ambulances
          if ((amb.arrived || amb.possiblyArrived) && amb.arrivalTime) {
            const elapsed = now.getTime() - amb.arrivalTime.getTime();
            const shouldKeep = elapsed < ARRIVAL_DISPLAY_DURATION;

            if (!shouldKeep) {
              const arrivalStatus = amb.arrived
                ? "arrived"
                : "possibly arrived";

              // Also remove from the ambulance store
              ambulanceStore.delete(amb.unitId);
            } else {
              // Log remaining time for debugging
              const timeLeft = ARRIVAL_DISPLAY_DURATION - elapsed;
              const arrivalStatus = amb.arrived
                ? "arrived"
                : "possibly arrived";
            }

            return shouldKeep;
          }
          return true;
        });

        // Sort ambulances:
        // 1. Non-arrived and non-possiblyArrived ambulances first (new ones at top, then by ETA)
        // 2. Possibly arrived ambulances next
        // 3. Arrived ambulances at the bottom
        finalAmbulances.sort((a, b) => {
          // First compare arrived status
          if (a.arrived && !b.arrived) return 1;
          if (!a.arrived && b.arrived) return -1;

          // If neither are officially arrived, compare possiblyArrived
          if (!a.arrived && !b.arrived) {
            if (a.possiblyArrived && !b.possiblyArrived) return 1;
            if (!a.possiblyArrived && b.possiblyArrived) return -1;
          }

          // If they have the same arrival status (both not arrived or both arrived)
          if (
            !a.arrived &&
            !b.arrived &&
            !a.possiblyArrived &&
            !b.possiblyArrived
          ) {
            // For non-arrived ambulances, new ones come first
            if (a.isNew && !b.isNew) return -1;
            if (!a.isNew && b.isNew) return 1;

            // Then sort by ETA if available
            if (a.eta && b.eta) return a.eta - b.eta;
            if (a.eta && !b.eta) return -1;
            if (!a.eta && b.eta) return 1;
          } else if (
            (a.arrived && b.arrived) ||
            (a.possiblyArrived && b.possiblyArrived)
          ) {
            // Both arrived or both possibly arrived, sort by arrival time (most recent first)
            return (
              (b.arrivalTime?.getTime() || 0) - (a.arrivalTime?.getTime() || 0)
            );
          }

          return 0;
        });

        callback(finalAmbulances);
      } catch (error) {
        console.error("Error resolving ambulance promises:", error);

        // Fallback: use whatever ambulances we have in the store
        const fallbackAmbulances = Array.from(ambulanceStore.values());

        callback(fallbackAmbulances);
      }
    } else {
      console.log("No new unique ambulances to process");
    }
  });

  // Return a combined unsubscribe function
  return () => {
    unsubscribeFromFirestore();
    unsubscribeFromLocationUpdates();

    // Clean up any active location listeners
    activeLocationListeners.forEach((locationRef, cacheKey) => {
      // We would unsubscribe from each location listener here if Firebase provided a way
      // Since onValue doesn't return an unsubscribe function in older Firebase versions,
      // we'll have to rely on garbage collection
    });
    activeLocationListeners.clear();
  };
};

export default db;
